import React from 'react';
import ChecklistIcon from 'App/assets/img/checklist-icon-big.png';
import {
	ActionContainer,
	ButtonDetails,
	ButtonStatus,
	ButtonTitle,
	Container,
	FormButton,
	LoadingContainer,
	ProgressContainer,
	Section,
	SectionCell,
	SectionHeader,
	SectionInfo,
} from './CollaboratorDetails.styles';
import { Feedback, ProgressBar, Spinner } from '@welbe/web-design-system';
import { RiskCard } from '../RiskCard/RiskCard';
import { useCollaboratorDetails } from './CollaboratorDetails.hooks';
import { ICollaboratorDetails, IEmployee } from './CollaboratorDetails.types';
import { ReactComponent as ErrorImage } from 'App/assets/img/sad-face.svg';
import { Button } from '@welbe/design-system';

const headers: IEmployee = {
	name: 'Colaborador',
	position: 'Puesto',
	branchOffice: 'Sucursal',
	sector: 'Sector',
	shift: 'Turno',
};

const employeeItems: string[] = [
	'name',
	'position',
	'branchOffice',
	'sector',
	'shift',
];

export const CollaboratorDetails = ({
	campaignId,
	employeeId,
	campaignAnswerId,
	onBackScreen,
}: ICollaboratorDetails): JSX.Element => {
	const {
		questionnaireSelected,
		loading,
		questionnaries,
		employee,
		hasError,
		refereceGuide,
		hasCategory,
		fetchCollaboratorDetails,
		setQuestionnaireSelected,
		riskStatus,
		riskName,
	} = useCollaboratorDetails({
		campaignId,
		employeeId,
		campaignAnswerId,
		onBackScreen,
	});

	if (hasError)
		return (
			<LoadingContainer>
				<Feedback
					Icon={ErrorImage}
					removeContainerIcon
					childrenButton="Intentar nuevamente"
					message="Ocurrió un error al intentar obtener la información del usuario."
					onRetry={fetchCollaboratorDetails}
				/>
			</LoadingContainer>
		);

	return (
		<React.Fragment>
			{loading ? (
				<LoadingContainer>
					<Spinner size="40px" />
				</LoadingContainer>
			) : (
				<>
					<Container>
						<Section columns={6}>
							{employeeItems.map((employeeItem, index) => (
								<SectionCell
									key={employeeItem}
									span={index === 0 ? '1 / span 2' : undefined}
								>
									<SectionHeader>{headers[employeeItem]}</SectionHeader>
									<SectionInfo>{employee[employeeItem] || '-'}</SectionInfo>
								</SectionCell>
							))}
						</Section>

						{questionnaries.map(
							(
								{ sequence, name, riskLevel, categories }: any,
								index: number,
							) => (
								<>
									<Section
										columns={6}
										key={sequence}
										noBorder={
											questionnaries.length - 1 === index ||
											questionnaireSelected === sequence
										}
										extraPadding={riskStatus(riskLevel.name)}
									>
										<SectionCell span="1 / span 2">
											<ButtonStatus
												danger={
													hasCategory(categories)
														? riskStatus(riskLevel.name)
														: false
												}
											>
												{riskName(riskLevel.name, categories)}
											</ButtonStatus>
											<FormButton
												onClick={() =>
													hasCategory(categories) &&
													setQuestionnaireSelected((prev) =>
														prev !== sequence ? sequence : undefined,
													)
												}
											>
												<img width="30px" src={ChecklistIcon} />
												<ButtonDetails>
													<ButtonTitle fontSize="12px">
														Guía de referencia {refereceGuide(sequence)}
													</ButtonTitle>
													<ButtonTitle>{name}</ButtonTitle>
												</ButtonDetails>
												{sequence > 0 && riskStatus(riskLevel.name) && (
													<ProgressContainer>
														<ProgressBar
															isDonut
															hasLabel
															backgroundColor="#D8DEE6"
															labelText={
																hasCategory(categories)
																	? riskLevel.name
																	: 'Sin datos'
															}
															labelFontSize={
																hasCategory(categories) ? '14px' : '12px'
															}
															labelColor="#64778F"
															progressColor={riskLevel.color}
															donutSize="60px"
															percentValue={hasCategory(categories) ? 100 : 0}
														/>
													</ProgressContainer>
												)}
											</FormButton>
										</SectionCell>

										<SectionCell span="3 / span 4">
											<SectionHeader color="#5082C2">
												Toma de acciónes:
											</SectionHeader>
											<SectionInfo variant>
												{hasCategory(categories)
													? riskLevel.action
													: 'Aún no hay respuesta.'}
											</SectionInfo>
										</SectionCell>
									</Section>
									<>
										{questionnaireSelected === sequence && (
											<Section
												noBorder={questionnaireSelected !== sequence}
												extraPadding
											>
												{categories.map((category: any) => (
													<RiskCard key={category} category={category} />
												))}
											</Section>
										)}
									</>
								</>
							),
						)}
					</Container>
					<ActionContainer>
						<Button width="140px" variant="outline" onClick={onBackScreen}>
							Regresar
						</Button>
					</ActionContainer>
				</>
			)}
		</React.Fragment>
	);
};
